import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ChevronLeft, Mail, Phone, MessageSquare, Building2, Send, Loader2, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://i.ibb.co/ZRpFcYDK/oie-s-XZ9-BUTHM5-Vz.png';

export default function ContactPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', subject: 'General Inquiry', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in your name, email, and message');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/contact`, form);
      setSubmitted(true);
      toast.success('Message sent! We will reach out within 24 hours.');
      setForm({ name: '', email: '', phone: '', company: '', subject: 'General Inquiry', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#020617] text-white relative overflow-hidden" data-testid="contact-page">
      {/* Ambient glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-amber-600/[0.08] rounded-full blur-[130px]" />
        <div className="absolute bottom-0 right-1/4 w-[450px] h-[450px] bg-cyan-600/[0.05] rounded-full blur-[110px]" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-white/[0.06] backdrop-blur-md bg-[#020617]/80">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-white/60 hover:text-white transition-colors" data-testid="contact-back-btn">
            <ChevronLeft className="w-4 h-4" />
            <img src={LOGO_URL} alt="DotCall" className="h-6 w-auto" />
          </button>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-white/60 hover:text-white text-sm hidden sm:inline-flex">Sign In</Button>
            <Button onClick={() => navigate('/pricing')} className="bg-amber-600 hover:bg-amber-500 text-white text-sm rounded-full px-5 font-semibold">Get Started</Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 py-14">
        <div className="text-center mb-12">
          <div className="inline-block px-3 py-1 rounded-full bg-amber-600/10 border border-amber-500/20 text-amber-300 text-xs font-semibold mb-4">Contact Us</div>
          <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] mb-3">
            Let's Talk About <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Your Business</span>
          </h1>
          <p className="text-white/50 text-base max-w-2xl mx-auto">
            Have questions about DotCall AI Voice Agents? Need a custom quote? Our team will get back to you within 24 hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left: Contact Info */}
          <div className="lg:col-span-2 space-y-4">
            <a href="tel:+918879851335" className="flex items-start gap-4 p-5 rounded-2xl bg-[#0B1221]/70 border border-white/[0.08] hover:border-amber-500/30 transition-all group" data-testid="contact-phone">
              <div className="w-11 h-11 rounded-xl bg-amber-600/15 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-600/25 transition-colors">
                <Phone className="w-5 h-5 text-amber-300" />
              </div>
              <div>
                <div className="text-xs text-white/40 uppercase tracking-wider mb-0.5">Call us</div>
                <div className="text-white font-semibold">+91 88798 51335</div>
                <div className="text-xs text-white/40 mt-1">Mon–Sat · 10 AM–7 PM IST</div>
              </div>
            </a>
            <a href="mailto:support@dotcall.io" className="flex items-start gap-4 p-5 rounded-2xl bg-[#0B1221]/70 border border-white/[0.08] hover:border-amber-500/30 transition-all group" data-testid="contact-email">
              <div className="w-11 h-11 rounded-xl bg-amber-600/15 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-600/25 transition-colors">
                <Mail className="w-5 h-5 text-amber-300" />
              </div>
              <div>
                <div className="text-xs text-white/40 uppercase tracking-wider mb-0.5">Email us</div>
                <div className="text-white font-semibold">support@dotcall.io</div>
                <div className="text-xs text-white/40 mt-1">We reply within 24 hours</div>
              </div>
            </a>
            <div className="flex items-start gap-4 p-5 rounded-2xl bg-[#0B1221]/70 border border-white/[0.08]">
              <div className="w-11 h-11 rounded-xl bg-amber-600/15 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-5 h-5 text-amber-300" />
              </div>
              <div>
                <div className="text-xs text-white/40 uppercase tracking-wider mb-0.5">Quick chat</div>
                <div className="text-white font-semibold">Use the DotCall AI Chat</div>
                <div className="text-xs text-white/40 mt-1">Bottom-left of every page</div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="p-6 md:p-8 rounded-2xl bg-[#0B1221]/80 border border-white/[0.08] space-y-4" data-testid="contact-form">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full bg-emerald-600/20 flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Thank you!</h3>
                  <p className="text-white/50 mb-6">Your message has been sent. Our team will reach out within 24 hours.</p>
                  <Button onClick={() => setSubmitted(false)} className="bg-amber-600 hover:bg-amber-500 text-white rounded-full px-6">Send Another Message</Button>
                </div>
              ) : (
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-white/70 text-sm">Full Name <span className="text-amber-400">*</span></Label>
                      <Input id="name" value={form.name} onChange={update('name')} placeholder="John Doe" className="bg-white/[0.04] border-white/10 text-white mt-1.5" data-testid="contact-name" />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-white/70 text-sm">Email <span className="text-amber-400">*</span></Label>
                      <Input id="email" type="email" value={form.email} onChange={update('email')} placeholder="john@company.com" className="bg-white/[0.04] border-white/10 text-white mt-1.5" data-testid="contact-email-input" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone" className="text-white/70 text-sm">Phone</Label>
                      <Input id="phone" value={form.phone} onChange={update('phone')} placeholder="+91 98765 43210" className="bg-white/[0.04] border-white/10 text-white mt-1.5" data-testid="contact-phone-input" />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-white/70 text-sm">Company</Label>
                      <Input id="company" value={form.company} onChange={update('company')} placeholder="Acme Retail Pvt. Ltd." className="bg-white/[0.04] border-white/10 text-white mt-1.5" data-testid="contact-company" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="subject" className="text-white/70 text-sm">What is this about?</Label>
                    <select id="subject" value={form.subject} onChange={update('subject')} className="w-full mt-1.5 h-10 rounded-md bg-white/[0.04] border border-white/10 text-white px-3 text-sm focus:border-amber-500/50 focus:outline-none" data-testid="contact-subject">
                      <option className="bg-[#0B1221]">General Inquiry</option>
                      <option className="bg-[#0B1221]">Pricing &amp; Plans</option>
                      <option className="bg-[#0B1221]">Demo Request</option>
                      <option className="bg-[#0B1221]">Enterprise / Custom Quote</option>
                      <option className="bg-[#0B1221]">Technical Support</option>
                      <option className="bg-[#0B1221]">Partnership / Reseller</option>
                      <option className="bg-[#0B1221]">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-white/70 text-sm">Message <span className="text-amber-400">*</span></Label>
                    <Textarea id="message" value={form.message} onChange={update('message')} rows={5} placeholder="Tell us what you are looking for — use case, expected call volume, languages, any specific needs..." className="bg-white/[0.04] border-white/10 text-white mt-1.5" data-testid="contact-message" />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-11 bg-amber-600 hover:bg-amber-500 text-white font-semibold rounded-full" data-testid="contact-submit">
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
                  </Button>
                  <p className="text-[11px] text-white/30 text-center">By submitting, you agree to our Privacy Policy and Terms &amp; Conditions.</p>
                </>
              )}
            </form>
          </div>
        </div>
      </main>

      <footer className="relative z-10 border-t border-white/[0.06] py-8 mt-8">
        <div className="max-w-6xl mx-auto px-6 text-center text-white/25 text-sm">
          Copyrights Under DotCall | All Rights Reserved
        </div>
      </footer>
    </div>
  );
}
